<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <reports-table
    module="time"
    name="attendance"
    :prefetch="false"
    :filters="serializedFilters"
    :column-count="3"
  >
    <template #default="{generateReport}">
      <oxd-table-filter
        :filter-title="$t('attendance.attendance_total_summary_report')"
      >
        <oxd-form @submitValid="generateReport">
          <oxd-form-row>
            <oxd-grid :cols="3" class="orangehrm-full-width-grid">
              <oxd-grid-item>
                <employee-autocomplete
                  v-model="filters.employee"
                  :rules="rules.employee"
                  :params="{
                    includeEmployees: 'currentAndPast',
                  }"
                />
              </oxd-grid-item>

              <oxd-grid-item>
                <jobtitle-dropdown v-model="filters.jobTitle" />
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model="filters.subunit"
                  type="select"
                  :label="$t('general.sub_unit')"
                  :options="subunits"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <employment-status-dropdown v-model="filters.empStatus" />
              </oxd-grid-item>
              <oxd-grid-item>
                <date-input
                  v-model="filters.fromDate"
                  :placeholder="$t('general.from')"
                  :rules="rules.fromDate"
                  :label="$t('general.date_range')"
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <date-input
                  v-model="filters.toDate"
                  label="&nbsp"
                  :placeholder="$t('general.to')"
                  :rules="rules.toDate"
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>

          <oxd-divider />

          <oxd-form-actions>
            <oxd-button 
              type="button"
              class="btn-export-attendance-summary"
              display-type="secondary"
              :label="$t('Export File')"
              @click="openModal"></oxd-button>
            <oxd-button
              type="submit"
              display-type="secondary"
              :label="$t('general.view')"
            />
          </oxd-form-actions> 
        </oxd-form>

        <div v-if="showModal" class="modal">
          <div class="modal-content">
            <div class="modal-header">
              <oxd-text type="card-title">Choose Time want to export</oxd-text>
            </div>
            <oxd-divider />
            <oxd-form @submit="exportReportFile" :loading="isLoading">
              <oxd-form-row>
                <oxd-input-field
                  v-model="exportModal.month"
                  :rules="rulesExportModal.month"
                  :label="$t('Month')"
                  required
                />
              </oxd-form-row>
              
              <oxd-form-row>
                <oxd-input-field
                  v-model="exportModal.year"
                  :rules="rulesExportModal.year"
                  :label="$t('Year')"
                  required
                />
              </oxd-form-row>
              
              <oxd-divider />

              <oxd-form-actions>
                <oxd-form-row>
                  <oxd-button
                    :label="$t('Cancel')"
                    display-type="text"
                    class="btn-cancel"
                    @click="onCancel"
                  />
                  <oxd-button
                    type="submit"
                    display-type="secondary"
                    :label="$t('Export')"
                  />
                </oxd-form-row>
              </oxd-form-actions>
            </oxd-form>
          </div>
        </div>
        
      </oxd-table-filter>
      <br />
    </template>

    <template #footer="{data}">
      {{ $t('time.total_duration') }}:
      {{ data.meta ? data.meta.sum.label : '0.00' }}
    </template>
  </reports-table>
</template>

<script>
import {computed, ref} from 'vue';
import axios from 'axios';
import {APIService} from '@/core/util/services/api.service';
import {
  validSelection,
  validDateFormat,
  endDateShouldBeAfterStartDate,
  startDateShouldBeBeforeEndDate,
  required, lessThanOrEqual, digitsOnly
} from '@/core/util/validation/rules';
import ReportsTable from '@/core/components/table/ReportsTable';
import JobtitleDropdown from '@/orangehrmPimPlugin/components/JobtitleDropdown';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import EmploymentStatusDropdown from '@/orangehrmPimPlugin/components/EmploymentStatusDropdown';
import usei18n from '@/core/util/composable/usei18n';
import useDateFormat from '@/core/util/composable/useDateFormat';

const now = new Date();
const year = now.getFullYear();
const month = now.getMonth();

function formatDate(date) {
  return `${String(year)}-${String(month + 1)}-${String(date.getDate())}`;
}

const defaultFilters = {
  employee: null,
  fromDate: formatDate(new Date(year, month, 1)),
  toDate: formatDate(new Date(year, month + 1, 0)),
  jobTitle: null,
  subunit: null,
  empStatus: null,
};

export default {
  components: {
    'reports-table': ReportsTable,
    'jobtitle-dropdown': JobtitleDropdown,
    'employee-autocomplete': EmployeeAutocomplete,
    'employment-status-dropdown': EmploymentStatusDropdown,
  },
  data() {
    return {
      showModal: false,
      baseUrl: window.appGlobal.baseUrl,
      isLoading: false,
      exportModal: {
        month: now.getMonth() + 1,
        year: now.getFullYear(),
      },
      rulesExportModal: {
        month: [
          required,
          digitsOnly,
          lessThanOrEqual(12),
        ],
        year: [
          required,
          digitsOnly,
          lessThanOrEqual(now.getFullYear()),
        ],
      }
    };
  },
  props: {
    subunits: {
      type: Array,
      default: () => [],
    },
  },

  setup() {
    const filters = ref({
      ...defaultFilters,
    });
    const {$t} = usei18n();
    const {userDateFormat} = useDateFormat();

    const rules = {
      fromDate: [
        validDateFormat(userDateFormat),
        startDateShouldBeBeforeEndDate(
          () => filters.value.toDate,
          $t('general.from_date_should_be_before_to_date'),
          {allowSameDate: true},
        ),
      ],
      toDate: [
        validDateFormat(userDateFormat),
        endDateShouldBeAfterStartDate(
          () => filters.value.fromDate,
          $t('general.to_date_should_be_after_from_date'),
          {allowSameDate: true},
        ),
      ],
      employee: [validSelection],
    };

    const serializedFilters = computed(() => {
      return {
        empNumber: filters.value.employee?.id,
        fromDate: filters.value.fromDate,
        toDate: filters.value.toDate,
        jobTitleId: filters.value.jobTitle?.id,
        subunitId: filters.value.subunit?.id,
        employmentStatusId: filters.value.empStatus?.id,
      };
    });

    return {
      rules,
      filters,
      serializedFilters,
    };
  },
  methods: {
    openModal() {
      this.showModal = true; // Mở modal
    },
    onCancel() {
      this.showModal = false; // close modal
    },
    isValidValueExport(params) {
      return params.month <= 12 && params.month > 0 && params.year <= now.getFullYear();
    },
    exportReportFile() {
      const params = {
        month: this.exportModal.month.toString().padStart(2, '0'),
        year: this.exportModal.year
      };

      if(!this.isValidValueExport(params)) return;

      this.isLoading = true;
      const http = new APIService(
        this.baseUrl,
        `api/v2/time/reports/data/export`,
      );
      http
        .request({
          type: 'GET',
          params: params,
          responseType: 'blob'
        })
        .then(response => {
          const url = URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = response.headers['content-name'];
          link.click()
          this.isLoading = false;
        });
    }
  },
};
</script>


<style lang="scss" scoped>
.btn-export-attendance-summary {
  margin-right: 10px;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: #fff;
  width: 400px;
  padding: 20px;
  border-radius: 10px;
}

.modal-header {
  margin-bottom: 0.8rem;
  display: flex;
  justify-content: center;
}

.btn-cancel {
    margin-right: 10px;
}
</style>